/** What a click on a map object means.
 *
 * The shell's click handler had exactly one reading of a click on the world:
 * "walk there". That is right for open ground and wrong for every structure on
 * the map, because a structure's own cells are impassable - `blocksMovement`
 * marks all of its footprint except its entrance - so `pathTo` returns nothing
 * and the click silently did nothing at all. A Windmill, a Shrine, a Mercenary
 * Camp: clicking any of them was a no-op no matter where the hero stood.
 *
 * The kernel already had the missing verb. `VISIT_OBJECT` visits an object the
 * hero is standing *next to*, which is exactly the case a path cannot express,
 * and it was one of the six commands nothing in the input layer could issue.
 *
 * A town is the same story told twice. It is a structure like any other - a
 * footprint that blocks, one doorstep that does not - but it is written down in
 * `state.towns` rather than `state.objects`, and the reading used to follow
 * that difference instead of the map: the doorstep was a walk, the roof matched
 * a `town`-kind object no visit can use, and a hero already standing on the
 * doorstep had nothing left to click at all. Three cells of one building, three
 * answers, one of them silence. `input.town` is that difference removed.
 *
 * This module is the reading, and it is pure so it can be tested without a
 * canvas: given where the hero is, what is on the clicked cell, and the
 * session's own reachability, it says which of the four things the click meant.
 * It decides no legality - `VISIT` is offered whenever the kernel's own
 * adjacency holds, and the kernel still refuses a visit it does not like.
 */

import type {
  HeroId, MapObjectDef, ObjectId, ObservedHero, ObservedObject, ObservedTown, TownId,
} from "@game/core";

export type MapClickAction =
  /** Walk. The path is the session's, cell indices excluding the origin. */
  | { readonly kind: "MOVE"; readonly hero: HeroId; readonly path: readonly number[] }
  /** Reach out and touch it from where the hero already stands. */
  | { readonly kind: "VISIT"; readonly hero: HeroId; readonly object: ObjectId }
  /** A town the hero has already walked up to. Whose it is decides what that
   *  means, and the kernel decides that, not the cursor. */
  | { readonly kind: "TOWN"; readonly hero: HeroId; readonly town: TownId }
  /** Nothing a click can do here. */
  | { readonly kind: "NONE" };

export const NO_MAP_ACTION: MapClickAction = Object.freeze({ kind: "NONE" });

/** Anything on the map that occupies a cell: an object's anchor, a town's
 *  doorstep, a hero. */
interface Placed {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** The kernel's adjacency for a reach: same level, Chebyshev distance <= 1.
 *  Distance 0 counts - a hero standing on a thing may reach out to it. */
export function adjacentToObject(hero: ObservedHero, at: Placed): boolean {
  return at.z === hero.z
    && Math.abs(at.x - hero.x) <= 1
    && Math.abs(at.y - hero.y) <= 1;
}

export interface MapClickInput {
  readonly hero: ObservedHero | null;
  /** What the click landed on, or null for open ground. */
  readonly object: ObservedObject | null;
  /** The definition of `object`, for its entrance offsets. */
  readonly def: MapObjectDef | null;
  /**
   * The town the click landed on - its doorstep or any cell of its building -
   * whoever owns it. Absent means "no town under the pointer"; it does not mean
   * "not mine", because the reading below is owner-blind on purpose.
   */
  readonly town?: ObservedTown | null;
  /** The session's path to the clicked cell; empty when it cannot be walked. */
  readonly path: readonly number[];
  /**
   * The session's own Dijkstra answer for one cell: the cheapest route to it
   * and what that route costs, or an empty path when it cannot be reached.
   * "Reachable" therefore means exactly what the wash on screen means.
   */
  readonly route: (x: number, y: number, z: number) => {
    readonly path: readonly number[];
    readonly cost: number;
  };
}

/**
 * Read one click.
 *
 * The order is the classic's. A cell you can walk to is a walk, even when something
 * is standing on it, because arriving *is* the visit; only when the cell cannot
 * be entered does the click become a reach - and if the hero is not yet within
 * reach, the click becomes the walk that puts him there.
 */
export function readMapClick(input: MapClickInput): MapClickAction {
  const hero = input.hero;
  if (hero === null) return NO_MAP_ACTION;

  const town = input.town ?? null;
  if (town !== null) return readTownClick(hero, town, input);

  if (input.path.length > 0) return { kind: "MOVE", hero: hero.id, path: input.path };

  const object = input.object;
  if (object === null) return NO_MAP_ACTION;

  // The two things on the map a click can do nothing with. `visitObject`'s
  // dispatch (`adventure/objects.ts`) resolves `hero` and `obstacle` to `DONE`
  // with no effect, and `enumerateLegalCommands` already refuses to offer a
  // visit on an obstacle; offering one anyway would be a click that emits an
  // `ObjectVisited` event and changes nothing, which is the exact class of
  // silent no-op this whole reading exists to remove.
  if (object.kind === "hero" || object.kind === "obstacle") return NO_MAP_ACTION;

  // A `town`-kind object reaching this far is a town with no `TownState`: the
  // generator builds neutral towns (`placeNeutralTowns`) and `newGame` can only
  // make a town for a player, so `obs.towns` has nothing to name and neither
  // has a visit. It is still a building with a doorstep, so the walk below is
  // the whole of what the click can honestly mean.
  if (object.kind !== "town" && adjacentToObject(hero, object)) {
    return { kind: "VISIT", hero: hero.id, object: object.id };
  }

  const best = cheapestLeg(input.route, approachCells(object, input.def), object.z);
  if (best.length === 0) return NO_MAP_ACTION;
  return { kind: "MOVE", hero: hero.id, path: best };
}

/**
 * A click on a town, whoever holds it.
 *
 * A town is a structure like every other one: a footprint that blocks and a
 * single doorstep that does not. The only thing that makes it different is
 * where it is written down - `state.towns` rather than `state.objects` - and
 * reading a click through that difference is what made an enemy town
 * unclickable. Its doorstep was a walk like any cell; its roof matched a
 * `town`-kind object, which no visit can do anything with, and so fell out of
 * the reading entirely; and once a hero stood on the doorstep there was
 * nothing left to walk to at all. Three cells of the same building, three
 * different answers, one of them silence.
 *
 * So the reading is one reading, and it is owner-blind. Walking onto the
 * doorstep is the arrival, and what arriving means - entering your own town,
 * assaulting someone else's - is the kernel's to decide, not the cursor's.
 * When there is no walk left to make, the click still names the town, so the
 * shell can put the question to the kernel and show its answer. A click that
 * changes nothing and says nothing is the one outcome this cannot produce.
 */
function readTownClick(
  hero: ObservedHero, town: ObservedTown, input: MapClickInput,
): MapClickAction {
  // The clicked cell itself is walkable - the doorstep, or a hero standing on
  // it. Arriving is the whole gesture.
  if (input.path.length > 0) return { kind: "MOVE", hero: hero.id, path: input.path };

  // Already at the gate: there is no walk left, so the town is the answer.
  if (adjacentToObject(hero, town)) return { kind: "TOWN", hero: hero.id, town: town.id };

  // The roof, or a doorstep this hero cannot step onto. Route to the doorstep
  // first and to the ring around it after, and let `MOVE_HERO` spend whatever
  // movement is left: a route longer than today's budget walks as far as it
  // reaches and stops, which is what a classic player expects of a long march.
  const leg = cheapestLeg(input.route, townApproachCells(town), town.z);
  if (leg.length > 0) return { kind: "MOVE", hero: hero.id, path: leg };

  // Nowhere to walk and too far to reach: the town is named anyway, so the
  // shell asks the kernel and the kernel's own refusal is what the player is
  // told. Saying "you cannot" is an answer; saying nothing is the bug.
  return { kind: "TOWN", hero: hero.id, town: town.id };
}

/** The cheapest of several routes, by the session's own cost. */
function cheapestLeg(
  route: MapClickInput["route"],
  cells: readonly (readonly [number, number])[],
  z: number,
): readonly number[] {
  let best: readonly number[] = [];
  let bestCost = Number.MAX_SAFE_INTEGER;
  for (const [x, y] of cells) {
    const leg = route(x, y, z);
    if (leg.path.length === 0 || leg.cost >= bestCost) continue;
    best = leg.path;
    bestCost = leg.cost;
  }
  return best;
}

/**
 * Where a hero can stand to use this object: its own entrances first, then the
 * ring around its anchor.
 *
 * Entrances are the cells the object's definition marks walkable inside its own
 * footprint, so standing on one *is* an arrival and the kernel visits without
 * being asked. The ring is the fallback for the objects that declare none.
 */
export function approachCells(
  object: Placed, def: MapObjectDef | null,
): readonly (readonly [number, number])[] {
  const out: (readonly [number, number])[] = [];
  const seen = new Set<string>();
  const add = (x: number, y: number): void => {
    const key = `${String(x)}:${String(y)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push([x, y]);
  };
  for (const [dx, dy] of def?.entrances ?? []) add(object.x + dx, object.y + dy);
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      add(object.x + dx, object.y + dy);
    }
  }
  return out;
}

/**
 * Where a hero can stand to use a town: the doorstep, then the ring around it.
 *
 * A town's own cell *is* its doorstep - `state.towns` records a town where a
 * hero walks in, not where its roof is drawn - so it leads the list, and a hero
 * who can reach it arrives rather than reaching out from beside it.
 */
export function townApproachCells(town: Placed): readonly (readonly [number, number])[] {
  return [[town.x, town.y] as const, ...approachCells(town, null)];
}

/**
 * The object drawn over a cell, footprint included.
 *
 * `PlayerObservation` reports an object at its anchor only, so a click on the
 * roof of a two-by-two Windmill matches nothing at all - the shell's own
 * `atCell` finds an object on one cell of a structure that covers four. The
 * footprint lives in the object's definition, so resolving it here is a lookup
 * rather than a rule. The anchor wins when two footprints overlap, which they
 * do not on a generated map (`place.ts` refuses an overlap) but might on a
 * hand-authored one.
 */
export function objectCovering(
  objects: readonly ObservedObject[],
  defOf: (def: string) => MapObjectDef | null,
  x: number, y: number, z: number,
): ObservedObject | null {
  let covering: ObservedObject | null = null;
  for (const o of objects) {
    if (o.z !== z) continue;
    if (o.x === x && o.y === y) return o;
    if (covering !== null) continue;
    const def = defOf(o.def);
    if (def === null) continue;
    for (const [dx, dy] of def.footprint) {
      if (o.x + dx === x && o.y + dy === y) { covering = o; break; }
    }
  }
  return covering;
}
